import React, { useState, createContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Chip, Tooltip,
  Divider, Menu, MenuItem, Badge,
} from '@mui/material';
import {
  FlashOn, Notifications, Menu as MenuIcon,
  ExitToApp, Circle,
  Public, SmartToy, WbCloudy, CalendarMonth, WbSunny,
  Explore, RocketLaunch, SatelliteAlt, Home, Nightlight, MenuBook,
  FeedOutlined, Satellite, Brightness3, Person, Login, PersonAdd,
  AccountCircle, Settings, WbIncandescent, ChevronLeft, ChevronRight,
  AdminPanelSettings,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import LiveAlertBanner from '../alerts/LiveAlertBanner';
import SpacePulseLogo from '../common/SpacePulseLogo';

export const DrawerContext = createContext(() => {});

const DRAWER_WIDTH           = 260;
const DRAWER_WIDTH_COLLAPSED = 64;

const navItems = [
  { label: 'Home',              path: '/',                   icon: <Home /> },
  { label: 'Space Weather',     path: '/dashboard',          icon: <WbSunny /> },
  { label: 'Space Events',      path: '/events',             icon: <FlashOn /> },
  { label: 'Earth Weather',     path: '/earth-weather',      icon: <WbCloudy /> },
  { label: 'NOVA AI Chat',      path: '/ai-chat',            icon: <SmartToy /> },
  { label: 'Solar System',      path: '/solar-system',       icon: <Public /> },
  { label: 'Satellite Tracker', path: '/satellite-tracker',  icon: <Satellite /> },
  { label: 'Live ISS Tracker',  path: '/iss-tracker',        icon: <SatelliteAlt /> },
  { label: 'Moon Dashboard',    path: '/moon',               icon: <Brightness3 /> },
  { label: 'Night Sky',         path: '/night-sky',          icon: <Nightlight /> },
  { label: 'Light Pollution Map', path: '/stargazing',       icon: <WbIncandescent /> },
  { label: 'Exoplanets',        path: '/exoplanets',         icon: <Explore /> },
  { label: 'Space News',        path: '/space-news',         icon: <FeedOutlined /> },
  { label: 'Study Zone',        path: '/study-zone',         icon: <MenuBook /> },
  { label: 'Space Missions',    path: '/space-missions',     icon: <RocketLaunch /> },
  { label: 'AstroTrack',        path: '/space-calendar',     icon: <CalendarMonth /> },
  { label: 'Telescope Feed',    path: '/telescope',          icon: <Explore /> },
  { label: 'My Alerts',         path: '/alerts',             icon: <Notifications /> },
];

export default function Layout() {
  const { user, logout, isGuest, isAdmin, openAuthModal, requireAuth } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mobileOpen, setMobileOpen]             = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileAnchor, setProfileAnchor]       = useState(null);

  const { connected, liveAlerts, dismissAlert } = useWebSocket();

  // ── Profile menu handlers ───────────────────────────────────────────────
  const handleProfileClick = (e) => {
    if (isGuest) {
      openAuthModal(0);
    } else {
      setProfileAnchor(e.currentTarget);
    }
  };
  const closeProfileMenu = () => setProfileAnchor(null);

  const goToProfile = () => { closeProfileMenu(); navigate('/profile'); };
  const handleLogout = () => { closeProfileMenu(); logout(); };

  // ── Drawer content ──────────────────────────────────────────────────────
  const DrawerContent = ({ collapsed = false }) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', overflow: 'hidden' }}>

      {/* ── Fixed Header ──────────────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{
          px: collapsed ? 0 : 2.5, pt: 2.5, pb: 1.5,
          display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start',
          transition: 'padding 0.25s',
        }}>
          <SpacePulseLogo size={36} showText={!collapsed} />
        </Box>

        {!collapsed && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              icon={<Circle sx={{ fontSize: '10px !important', color: connected ? '#66bb6a !important' : '#ef5350 !important' }} />}
              label={connected ? 'Live' : 'Offline'}
              sx={{
                bgcolor: connected ? 'rgba(102,187,106,0.1)' : 'rgba(239,83,80,0.1)',
                color: connected ? '#66bb6a' : '#ef5350',
                border: 'none', fontSize: 11,
              }}
            />
            {isGuest && (
              <Chip
                size="small"
                label="Guest"
                sx={{
                  bgcolor: 'rgba(255,167,38,0.1)',
                  color: '#ffa726',
                  border: '1px solid rgba(255,167,38,0.3)',
                  fontSize: 11,
                }}
              />
            )}
          </Box>
        )}

        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}>
            <Circle sx={{ fontSize: 10, color: connected ? '#66bb6a' : '#ef5350' }} />
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      </Box>

      {/* ── Scrollable Nav ─────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' },
        },
      }}>
        <List sx={{ px: collapsed ? 0.5 : 1, py: 1 }}>
          {isAdmin && (() => {
            const active = location.pathname.startsWith('/admin');
            const btn = (
              <ListItemButton
                key="/admin"
                onClick={() => { navigate('/admin'); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2, mb: 0.5,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 2,
                  bgcolor: active ? 'rgba(206,147,216,0.15)' : 'transparent',
                  borderLeft: active ? '3px solid #ce93d8' : '3px solid transparent',
                  '&:hover': { bgcolor: 'rgba(206,147,216,0.1)' },
                  transition: 'all 0.15s',
                }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: active ? '#ce93d8' : '#ce93d8', justifyContent: 'center' }}>
                  <AdminPanelSettings />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="Admin Dashboard"
                    primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 400, color: '#ce93d8' }}
                  />
                )}
              </ListItemButton>
            );
            return collapsed
              ? <Tooltip key="/admin" title="Admin Dashboard" placement="right">{btn}</Tooltip>
              : <React.Fragment key="/admin">{btn}</React.Fragment>;
          })()}
          {navItems.map((item) => {
            const active = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);

            // Alerts require login — show lock badge for guests
            const restricted = item.path === '/alerts' && isGuest;

            const button = (
              <ListItemButton
                key={item.path}
                onClick={() => {
                  if (restricted) {
                    requireAuth('Login required to manage your alerts 🚀');
                    return;
                  }
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2, mb: 0.5,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 2,
                  bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                  borderLeft: active ? '3px solid' : '3px solid transparent',
                  borderLeftColor: active ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  transition: 'all 0.15s',
                  opacity: restricted ? 0.6 : 1,
                }}
              >
                <ListItemIcon sx={{
                  minWidth: collapsed ? 0 : 40,
                  color: active ? 'primary.main' : 'text.secondary',
                  justifyContent: 'center',
                }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: active ? 700 : 400,
                      color: active ? 'primary.light' : 'text.secondary',
                    }}
                  />
                )}
                {!collapsed && restricted && (
                  <Typography sx={{ fontSize: 12, color: 'rgba(255,167,38,0.7)' }}>🔒</Typography>
                )}
              </ListItemButton>
            );

            return collapsed
              ? <Tooltip key={item.path} title={item.label} placement="right">{button}</Tooltip>
              : <React.Fragment key={item.path}>{button}</React.Fragment>;
          })}
        </List>
      </Box>

      {/* ── Fixed Footer ───────────────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        {isGuest ? (
          <Box sx={{ p: collapsed ? 1 : 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: collapsed ? 'center' : 'stretch' }}>
            <Tooltip title={collapsed ? 'Sign In' : ''} placement="right">
              <Box
                onClick={() => openAuthModal(0)}
                sx={{
                  display: 'flex', alignItems: 'center',
                  gap: collapsed ? 0 : 1.5,
                  p: collapsed ? 1 : 1.5,
                  borderRadius: 2, cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.2)',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  transition: 'all 0.15s',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                }}
              >
                <Login sx={{ color: 'primary.main', fontSize: 18 }} />
                {!collapsed && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.light', lineHeight: 1.1 }}>
                      Sign In
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Unlock all features
                    </Typography>
                  </Box>
                )}
              </Box>
            </Tooltip>
            {!collapsed && (
              <Box
                onClick={() => openAuthModal(1)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1,
                  borderRadius: 2, cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                  transition: 'all 0.15s',
                }}
              >
                <PersonAdd sx={{ color: 'text.secondary', fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Create free account
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Tooltip title={collapsed ? (user?.name || '') : ''} placement="right">
            <Box sx={{
              p: collapsed ? 1 : 2,
              display: 'flex', alignItems: 'center',
              gap: collapsed ? 0 : 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}>
              <Avatar
                src={user?.profilePhoto}
                sx={{ width: 36, height: 36, bgcolor: 'primary.dark', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                onClick={goToProfile}
              >
                {user?.name?.[0]?.toUpperCase()}
              </Avatar>
              {!collapsed && (
                <>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                      {user?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {user?.accountType === 'PRO' ? '⭐ Pro Member' : user?.role}
                    </Typography>
                  </Box>
                  <Tooltip title="Logout">
                    <IconButton size="small" onClick={logout} sx={{ color: 'text.secondary' }}>
                      <ExitToApp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  // ── Profile avatar button (top-right of every AppBar) ───────────────────
  const ProfileButton = () => (
    <Tooltip title={isGuest ? 'Sign in / Create account' : 'My Profile'}>
      <IconButton
        onClick={handleProfileClick}
        sx={{
          p: 0.5,
          border: '2px solid',
          borderColor: isGuest ? 'rgba(255,167,38,0.4)' : 'rgba(255,255,255,0.3)',
          borderRadius: '50%',
          '&:hover': {
            borderColor: isGuest ? '#ffa726' : 'primary.main',
            bgcolor: 'rgba(255,255,255,0.08)',
          },
          transition: 'all 0.2s',
        }}
      >
        {isGuest ? (
          <Person sx={{ color: '#ffa726', fontSize: 22 }} />
        ) : (
          <Avatar
            src={user?.profilePhoto}
            sx={{ width: 28, height: 28, bgcolor: 'primary.dark', fontSize: 13, fontWeight: 700 }}
          >
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
        )}
      </IconButton>
    </Tooltip>
  );

  const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <DrawerContext.Provider value={setMobileOpen}>
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* ── Desktop sidebar with collapse toggle ──────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          flexShrink: 0,
          width: drawerWidth,
          transition: 'width 0.25s ease',
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            transition: 'width 0.25s ease',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
              transition: 'width 0.25s ease',
            },
          }}
        >
          <DrawerContent collapsed={sidebarCollapsed} />
        </Drawer>

        {/* Collapse / expand toggle button */}
        <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={() => setSidebarCollapsed(prev => !prev)}
            size="small"
            sx={{
              position: 'fixed',
              top: '50%',
              left: drawerWidth,
              transform: 'translate(-50%, -50%)',
              zIndex: 1300,
              width: 22,
              height: 44,
              borderRadius: '0 6px 6px 0',
              bgcolor: 'background.paper',
              border: '1px solid rgba(255,255,255,0.15)',
              borderLeft: 'none',
              color: 'text.secondary',
              transition: 'left 0.25s ease, background-color 0.15s',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
              },
              '& .MuiSvgIcon-root': { fontSize: 16 },
            }}
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, overflow: 'hidden' },
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'clip', minWidth: 0 }}>

        {/* ── AppBar (visible on all screen sizes) ────────────────────── */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            top: 0,
            zIndex: 1200,
            bgcolor: 'background.paper',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Toolbar sx={{ minHeight: '52px !important', px: { xs: 1.5, md: 2 } }}>
            {/* Mobile: hamburger */}
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ color: 'primary.main', mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Title (mobile only — desktop has sidebar branding) */}
            <Box sx={{ flex: 1, display: { md: 'none' } }}>
              <SpacePulseLogo size={28} showText />
            </Box>

            {/* Desktop spacer */}
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

            {/* Live alerts indicator */}
            {!isGuest && liveAlerts.length > 0 && (
              <Badge badgeContent={liveAlerts.length} color="error" sx={{ mr: 1.5 }}>
                <Notifications sx={{ color: 'text.secondary', fontSize: 20 }} />
              </Badge>
            )}

            {/* Guest mode label */}
            {isGuest && (
              <Chip
                label="Guest Mode"
                size="small"
                sx={{
                  mr: 1.5, fontSize: 11, height: 24,
                  bgcolor: 'rgba(255,167,38,0.1)',
                  color: '#ffa726',
                  border: '1px solid rgba(255,167,38,0.3)',
                }}
              />
            )}

            {/* Profile / Avatar button */}
            <ProfileButton />
          </Toolbar>
        </AppBar>

        {/* Profile dropdown menu (authenticated users) */}
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={closeProfileMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 0.5, minWidth: 180,
              bgcolor: 'background.paper',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 2,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
              {user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {user?.email}
            </Typography>
          </Box>
          <MenuItem onClick={goToProfile} sx={{ gap: 1.5, py: 1.2 }}>
            <AccountCircle sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2">My Profile</Typography>
          </MenuItem>
          <MenuItem onClick={() => { closeProfileMenu(); navigate('/alerts'); }} sx={{ gap: 1.5, py: 1.2 }}>
            <Notifications sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">My Alerts</Typography>
          </MenuItem>
          <MenuItem onClick={() => { closeProfileMenu(); navigate('/profile?tab=settings'); }} sx={{ gap: 1.5, py: 1.2 }}>
            <Settings sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2">Settings</Typography>
          </MenuItem>
          {isAdmin && (
            <MenuItem onClick={() => { closeProfileMenu(); navigate('/admin'); }} sx={{ gap: 1.5, py: 1.2 }}>
              <AdminPanelSettings sx={{ fontSize: 18, color: '#ce93d8' }} />
              <Typography variant="body2" sx={{ color: '#ce93d8' }}>Admin Dashboard</Typography>
            </MenuItem>
          )}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
          <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.2 }}>
            <ExitToApp sx={{ fontSize: 18, color: '#ef5350' }} />
            <Typography variant="body2" sx={{ color: '#ef5350' }}>Sign Out</Typography>
          </MenuItem>
        </Menu>

        {/* Live alert banners */}
        <Box sx={{ position: 'fixed', top: 64, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {liveAlerts.map(alert => (
            <LiveAlertBanner key={alert.id} alert={alert} onDismiss={() => dismissAlert(alert.id)} />
          ))}
        </Box>

        {/* Page content */}
        <Box component="main" sx={{
          flex: 1,
          overflow: 'auto',
          p: location.pathname === '/' ? 0 : { xs: 2, md: 3 },
          scrollSnapType: location.pathname === '/' ? { md: 'y mandatory' } : 'none',
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
    </DrawerContext.Provider>
  );
}
